import { useRecordContext, useTranslate } from "ra-core";
import { EditButton } from "@/components/admin/edit-button";
import { CallTranscript } from "./CallTranscript";
import { ContactBlockBanner, useCompanyBlock } from "../companies/ContactBlock";
import { ContactSalutation } from "./ContactSalutation";
import { DeleteButton } from "@/components/admin";
import { ReferenceManyField } from "@/components/admin/reference-many-field";
import { ShowButton } from "@/components/admin/show-button";

import { AddTask } from "../tasks/AddTask";
import { TasksIterator } from "../tasks/TasksIterator";
import { TagsListEdit } from "./TagsListEdit";
import { ContactStatusSelector } from "./ContactInputs";
import { ContactPersonalInfo } from "./ContactPersonalInfo";
import { ContactBackgroundInfo } from "./ContactBackgroundInfo";
import { AsideSection } from "../misc/AsideSection";
import { GreetingButton, GreetingsList } from "../greetings/GreetingDialog";
import type { Contact } from "../types";
import { ContactMergeButton } from "./ContactMergeButton";
import { ExportVCardButton } from "./ExportVCardButton";
import { ParticipationsAside } from "../deals/DealParticipants";

export const ContactAside = ({ link = "edit" }: { link?: "edit" | "show" }) => {
  const record = useRecordContext<Contact>();
  const translate = useTranslate();
  const blockedCompany = useCompanyBlock(record?.company_id);

  if (!record) return null;

  return (
    <div className="hidden sm:block w-64 min-w-64 lg:w-92 lg:min-w-92 text-sm">
      <div className="mb-4 -ml-1 flex flex-wrap items-center gap-2">
        {link === "edit" ? (
          <EditButton label="resources.contacts.action.edit" />
        ) : (
          <ShowButton label="resources.contacts.action.show" />
        )}
        {!blockedCompany && <CallTranscript contact={record} />}
        <GreetingButton record={record} />
      </div>
      <ContactBlockBanner company={blockedCompany} />

      <ContactSalutation record={record} />

      <AsideSection title={translate("resources.notes.fields.status")}>
        <ContactStatusSelector />
      </AsideSection>

      <AsideSection
        title={translate("resources.contacts.field_categories.personal_info")}
      >
        <ContactPersonalInfo />
      </AsideSection>

      <ParticipationsAside contactId={record.id} />

      <GreetingsAside contactId={record.id} />

      <AsideSection
        title={translate("resources.contacts.field_categories.background_info")}
      >
        <ContactBackgroundInfo />
      </AsideSection>

      <AsideSection
        title={translate("resources.tags.name", { smart_count: 2 })}
      >
        <TagsListEdit />
      </AsideSection>

      <AsideSection
        title={translate("resources.tasks.name", { smart_count: 2 })}
      >
        <ReferenceManyField
          target="contact_id"
          reference="tasks"
          sort={{ field: "due_date", order: "ASC" }}
          perPage={1000}
        >
          <TasksIterator />
        </ReferenceManyField>
        <AddTask />
      </AsideSection>

      {link !== "edit" && (
        <>
          <div className="mt-6 pt-6 border-t hidden sm:flex flex-col gap-2 items-start">
            <ExportVCardButton />
            <ContactMergeButton />
          </div>
          <div className="mt-6 pt-6 border-t hidden sm:flex flex-col gap-2 items-start">
            <DeleteButton
              className="h-6 cursor-pointer hover:bg-destructive/10! text-destructive! border-destructive! focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40"
              size="sm"
            />
          </div>
        </>
      )}
    </div>
  );
};

// LDF: gesendete Blumengrüße mit „geöffnet“-Status (nur wenn schon welche verschickt wurden)
const GreetingsAside = ({ contactId }: { contactId: number | string }) => (
  <div className="mb-6 empty:hidden [&:not(:has(li))]:hidden">
    <h3 className="mb-2 text-sm font-semibold text-muted-foreground">Blumengrüße</h3>
    <GreetingsList contactId={contactId} />
  </div>
);
